import axios from "./axyos";
import type { CountryValue } from "../features/country/countrySlice";

export const updateUserRoleByIdApi = (userId: string, role: string) =>
  axios.post(`/users/${encodeURIComponent(userId)}/role`, { role });

export const getAdminUserLoginLocationsApi = () =>
  axios.get("/admin/user-login-locations");

export type AdminUserSearchResult = {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  phone: string;
  indicatif: string;
  codeCountry: string;
  country: CountryValue;
  status: string;
  role: string;
  status_compte: boolean;
  nbr_child: number;
  created_at: string | null;
  updated_at: string | null;
};

export type AdminUserSort = "created_at" | "lastLoginAt";

export const searchAdminUsersApi = (query: string, limit = 20, cursor?: string, signal?: AbortSignal, sortBy: AdminUserSort = "created_at") =>
  axios.get<{ data: AdminUserSearchResult[]; count: number; status: number; hasMore: boolean; nextCursor: string | null }>("/admin/users/search", {
    params: { q: query, limit, cursor, sortBy },
    signal,
  });

export const deleteAdminUserCascadeApi = (userId: string) =>
  axios.delete<{ data: { deleted: Record<string, number> }; status: number; message: string }>(
    `/admin/users/${encodeURIComponent(userId)}/cascade`
  );

export type ServerMetricsInsight = {
  level: "info" | "warn" | "critical";
  code: string;
  meta?: {
    days?: number;
  };
};

export type ServerMetricsHistoryPoint = {
  timestamp: string;
  disk: {
    uploadDir: string;
    uploadBytes: number;
    uploadFiles: number;
    totalBytes: number;
    freeBytes: number;
    usedBytes: number;
    usedPercent: number;
    uploadPercentOfDisk: number;
  };
  memory: {
    totalBytes: number;
    freeBytes: number;
    usedBytes: number;
    usedPercent: number;
  };
  cpu: {
    cores: number;
    load1: number;
    load5: number;
    load15: number;
    loadPercent: number;
  };
  process: {
    pid: number;
    uptimeSeconds: number;
    rssBytes: number;
    heapTotalBytes: number;
    heapUsedBytes: number;
    externalBytes: number;
  };
};

export type ServerMetricsPayload = {
  collectedAt: string;
  sampleIntervalMinutes: number;
  windowHours: number;
  current: ServerMetricsHistoryPoint | null;
  history: ServerMetricsHistoryPoint[];
  summary: {
    disk: {
      usedPercent: number;
      uploadBytes: number;
      uploadFiles: number;
      freeBytes: number;
      totalBytes: number;
      uploadGrowth24hBytes: number;
      uploadGrowth24hPercent: number;
      estimatedDaysUntilFull: number | null;
      peakPercent24h: number;
    };
    memory: {
      usedPercent: number;
      usedBytes: number;
      totalBytes: number;
      avgPercent24h: number;
      peakPercent24h: number;
    };
    cpu: {
      currentPercent: number;
      avgPercent24h: number;
      peakPercent24h: number;
      cores: number;
    };
    process: {
      rssBytes: number;
      heapUsedBytes: number;
      uptimeSeconds: number;
    };
  } | null;
  insights: ServerMetricsInsight[];
};

export const getAdminServerMetricsApi = () =>
  axios.get<{ data: ServerMetricsPayload }>("/admin/server-metrics");

export type UploadAuditOrphanFile = {
  filename: string;
  absolutePath: string;
  relativePath: string;
  sizeBytes: number;
  updatedAt: string;
  urlPath: string;
  matchedPhotos: Array<{
    photoId: string;
    adId: string | null;
    adTitle: string | null;
  }>;
};

export type UploadAuditPayload = {
  uploadDir: string;
  scannedAt: string;
  totalFilesScanned: number;
  linkedFilesCount: number;
  orphanFilesCount: number;
  orphanFiles: UploadAuditOrphanFile[];
};

export const auditAdminUploadImagesApi = () =>
  axios.get<{ data: UploadAuditPayload }>("/admin/upload-images/audit");

export const deleteAuditedUploadImageApi = (params: { filename: string; relativePath?: string; absolutePath?: string }) =>
  axios.delete<{ data: { filename: string; deletedPhotoEntriesCount: number } }>("/admin/upload-images/audit", {
    data: params,
  });
